import { BoxIcon, UtensilsCrossed, Leaf, Medal, Recycle, ThermometerSun, TruckIcon, ExternalLink, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DPPCard from '../../components/product/DPPCard';
import BottomNavbar from '../../layouts/BottomNavbar';
import TopBar from '../../layouts/TopBar';
import MenuSidebar from '../../layouts/MenuSidebar';
import { darkTheme, lightTheme } from '../../theme/theme';
import { getActiveProduct, getProductDataFolder, PRODUCTS } from '../../utils/urlConfig';
import useTheme from '../../hooks/useTheme';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const DPPScreen = () => {
    const [isDark, setIsDark] = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeCard, setActiveCard] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [productData, setProductData] = useState(null);
    const [activeVideo, setActiveVideo] = useState(null);
    const [activeProduct, setActiveProductState] = useState('');
    const navigate = useNavigate();
    const { productId: urlProductId, serialNumber: urlSerialNumber } = useParams();

    // Push a history entry when a card opens so the hardware/browser back button closes it
    const openCard = (card) => {
        setActiveCard(card);
        setExpandedSections({});
        window.history.pushState({ dppCard: true }, '');
    };

    useEffect(() => {
        const handlePopState = () => {
            if (activeCard) {
                setActiveCard(null);
                setExpandedSections({});
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [activeCard]);
    const theme = isDark ? darkTheme : lightTheme;

    useEffect(() => {
        if (urlProductId && urlSerialNumber) {
            let product = getActiveProduct();
            for (const [key, config] of Object.entries(PRODUCTS)) {
                if (config.gtin === urlProductId) {
                    product = key;
                    break;
                }
            }
            setActiveProductState(product);
        } else {
            setActiveProductState(getActiveProduct());
        }
    }, [urlProductId, urlSerialNumber]);

    useEffect(() => {
        // Load all product data files dynamically
        const loadProductData = async () => {
            try {
                const dataFolder = getProductDataFolder(activeProduct);
                
                const [
                    productIdData,
                    materialData,
                    supplyChainData,
                    sustainabilityData,
                    maintenanceData,
                    recyclingData,
                    complianceData
                ] = await Promise.all([
                    import(`../../data/${dataFolder}/product_identification.json`),
                    import(`../../data/${dataFolder}/material_component_information.json`),
                    import(`../../data/${dataFolder}/supply_chain_origin.json`),
                    import(`../../data/${dataFolder}/sustainability_environmental.json`),
                    import(`../../data/${dataFolder}/maintenance_repair_service.json`),
                    import(`../../data/${dataFolder}/recycling_end_of_life.json`),
                    import(`../../data/${dataFolder}/compliance_certifications.json`)
                ]);

                setProductData({
                    productIdData,
                    materialData,
                    supplyChainData,
                    sustainabilityData,
                    maintenanceData,
                    recyclingData,
                    complianceData
                });
            } catch (error) {
                console.error('Error loading product data:', error);
            }
        };

        loadProductData();
    }, [activeProduct]);

    // Build cards array from loaded product data
    const cards = productData ? [
        {
            id: 'product-id',
            title: productData.productIdData.header.title,
            icon: BoxIcon,
            iconBg: 'rgba(220, 184, 255, 0.35)',
            iconColor: '#A855F7',
            data: productData.productIdData.content
        },
        {
            id: 'material',
            title: productData.materialData.header.title,
            icon: UtensilsCrossed,
            iconBg: 'rgba(246, 184, 255, 0.35)',
            iconColor: '#EC4899',
            data: productData.materialData.content
        },
        {
            id: 'supply-chain',
            title: productData.supplyChainData.header.title,
            icon: TruckIcon,
            iconBg: 'rgba(184, 227, 255, 0.35)',
            iconColor: '#3B82F6',
            data: productData.supplyChainData.content
        },
        {
            id: 'sustainability',
            title: productData.sustainabilityData.header.title,
            icon: Leaf,
            iconBg: 'rgba(184, 255, 207, 0.42)',
            iconColor: '#22C55E',
            data: productData.sustainabilityData.content
        },
        {
            id: 'maintenance',
            title: productData.maintenanceData.header.title,
            icon: ThermometerSun,
            iconBg: 'rgba(211, 178, 166, 0.4)',
            iconColor: '#A1887F',
            data: productData.maintenanceData.content
        },
        {
            id: 'recycling',
            title: productData.recyclingData.header.title,
            icon: Recycle,
            iconBg: 'rgba(187, 238, 237, 0.4)',
            iconColor: '#26C6DA',
            data: productData.recyclingData.content
        },
        {
            id: 'compliance',
            title: productData.complianceData.header.title,
            icon: Medal,
            iconBg: 'rgba(252, 207, 156, 0.4)',
            iconColor: '#FF9F80',
            data: productData.complianceData.content
        }
    ] : [];

    return (
        <div className="h-screen flex flex-col" style={{ backgroundColor: theme.background1 }}>
            {/* Header */}
            <TopBar
                theme={theme}
                isDark={isDark}
                setIsDark={setIsDark}
                title="DPP"
                onBackClick={() => {
                    const baseUrl = urlProductId && urlSerialNumber 
                        ? `/01/${urlProductId}/21/${urlSerialNumber}/verification`
                        : `/01/${PRODUCTS[activeProduct]?.gtin}/21/${PRODUCTS[activeProduct]?.serialNumber}/verification`;
                    
                    // Retrieve and append the last verification status
                    const savedStatus = localStorage.getItem('lastVerificationStatus');
                    const backPath = savedStatus ? `${baseUrl}/${savedStatus}` : baseUrl;
                    
                    navigate(backPath, { state: { isDark } });
                }}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
            />

            {/* Menu Sidebar */}
            <MenuSidebar
                theme={theme}
                isDark={isDark}
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
            />

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 pt-20 pb-24">
                {!productData && <LoadingSpinner />}
                <div className="flex flex-col w-full mt-4">
                    {cards.map((card, index) => (
                        <DPPCard
                            key={card.id}
                            theme={theme}
                            title={card.title}
                            icon={card.icon}
                            iconBgColor={card.iconBg}
                            iconColor={card.iconColor}
                            
                            onClick={() => openCard(card)}
                            className={index === 0 ? "" : "mt-4"}
                        />
                    ))}
                </div>
            </div>

            {/* Generic Popup */}
            {activeCard && (
                <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full h-full z-[9999] flex flex-col" style={{ backgroundColor: theme.background2 }}>
                    {/* Fixed Header */}
                    <div className="flex items-center justify-between w-full p-6 pb-4 flex-shrink-0">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
                                style={{ backgroundColor: activeCard.iconBg }}>
                                <activeCard.icon size={20} style={{ color: activeCard.iconColor }} />
                            </div>
                            <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
                                {activeCard.title}
                            </h2>
                        </div>
                        <button
                            onClick={() => window.history.back()}
                            className="p-1"
                            style={{ color: theme.gray }}
                        >
                            <XCircle size={24} />
                        </button>
                    </div>
                    
                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto px-6 pb-6">
                        <div className="flex flex-col items-start justify-start text-left py-4 w-full rounded-lg gap-4"
                            style={{ backgroundColor: theme.background2 }}>
                            
                            {activeCard.data && activeCard.data.length > 0 ? (
                                activeCard.data.map((item, index) => {
                                    if (item.type === 'section') {
                                        const isExpanded = expandedSections[index];
                                        const defaultCount = item.defaultVisible || 4;
                                        const visibleItems = (item.action && !isExpanded && item.items.length > defaultCount) 
                                            ? item.items.slice(0, defaultCount) 
                                            : item.items;

                                        return (
                                            <div key={index} className="w-full mb-4">
                                                <div 
                                                    className="w-full p-3 rounded-md mb-2 font-semibold"
                                                    style={{ backgroundColor: theme.stroke, color: theme.text }}
                                                >
                                                    {item.title}
                                                </div>
                                                <div className="px-2">
                                                    {item.listType === 'simple' ? (
                                                        <ul className="list-none space-y-2">
                                                            {visibleItems.map((subItem, subIndex) => (
                                                                <li key={subIndex} style={{ color: theme.text }}>
                                                                    {subItem}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        visibleItems.map((subItem, subIndex) => (
                                                            <div 
                                                                key={subIndex} 
                                                                className={`mb-4 ${(subItem.label === 'Supplier / Origin' || subItem.label === 'Recycling Path') ? 'border-b pb-4' : ''}`}
                                                                style={{ borderColor: theme.stroke }}
                                                            >
                                                                <p className="text-sm font-medium" style={{ color: theme.gray }}>{subItem.label}</p>
                                                                <p className="font-regular" style={{ color: theme.text }}>{subItem.value}</p>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                                {item.action && (
                                                    <button 
                                                        onClick={() => setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }))}
                                                        className="w-full mt-2 py-2 rounded-md border text-sm font-medium transition-colors hover:bg-opacity-10"
                                                        style={{ 
                                                            borderColor: theme.button, 
                                                            color: theme.button 
                                                        }}
                                                    >
                                                        {isExpanded ? "Show Less" : item.action}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    }
                                    
                                    // Default rendering for simple key-value pairs
                                    if (item.type === 'video') {
                                        return (
                                            <div key={index} className="border-b w-full pb-4 mb-2" style={{ borderColor: theme.stroke }}>
                                                <p className="text-sm" style={{ color: theme.gray }}>{item.label}:</p>
                                                <button
                                                    type="button"
                                                    onClick={() => setActiveVideo(item)}
                                                    className="font-regular underline text-left"
                                                    style={{ color: theme.button }}
                                                >
                                                    {item.value}
                                                </button>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={index} className="border-b w-full pb-4 mb-2" style={{ borderColor: theme.stroke }}>
                                            <p className="text-sm" style={{ color: theme.gray }}>{item.label}:</p>
                                            <p className="font-regular" style={{ color: theme.text }}>{item.value}</p>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-center w-full" style={{ color: theme.gray }}>No details available.</p>
                            )}

                        </div>
                    </div>
                </div>
            )}

            {activeVideo && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', pointerEvents: 'auto' }}>
                    <div className="relative z-10 w-full max-w-3xl rounded-lg overflow-hidden" style={{ backgroundColor: theme.background2, pointerEvents: 'auto' }}>
                        <div className="flex items-center justify-between p-4">
                            <h2 className="font-semibold" style={{ color: theme.text }}>{activeVideo.label}</h2>
                            <button
                                type="button"
                                onClick={() => setActiveVideo(null)}
                                className="p-1"
                                aria-label="Close video"
                                style={{ color: theme.gray }}
                            >
                                <XCircle size={24} />
                            </button>
                        </div>
                        <div className="w-full aspect-video">
                            <iframe
                                className="relative z-10 w-full h-full"
                                src={`${activeVideo.videoUrl}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                                title={activeVideo.label}
                                referrerPolicy="origin"
                                loading="eager"
                                style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
                                allow="autoplay; encrypted-media; fullscreen; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        </div>
                        <a
                            href={`https://www.youtube.com/watch?v=${activeVideo.videoId}&t=2s`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium"
                            style={{ color: theme.button, borderTop: `1px solid ${theme.stroke}` }}
                        >
                            <ExternalLink size={16} />
                            Watch on YouTube
                        </a>
                    </div>
                </div>
            )}

            {/* Bottom Navigation */}
            <BottomNavbar theme={theme} isDark={isDark} />

        </div>
    );
};

export default DPPScreen;
